/**
 * "Referral code (optional)" on the room application.
 *
 * Sits with the documents, immediately above Submit — the last thing an
 * applicant fills in, and the place they are most likely to remember the code a
 * friend told them.
 *
 * Validation is live but never blocking. An unrecognised code shows a warning
 * and the application still submits: losing a completed application (and, for a
 * logged-out applicant, the account and uploads created with it) over a mistyped
 * code would be a far worse outcome than an unattributed referral. The same
 * reasoning is why attach_referral_to_booking returns {ok:false, reason} instead
 * of raising.
 *
 * The lookup is public (an applicant is often logged out at this point) and
 * returns only a masked first name — see resolve_referral_code in
 * 20260907170100_referral_capture_and_lookup.sql.
 */

import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, Share2, TriangleAlert } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";
import {
  normaliseReferralCode,
  EMPTY_REFERRAL_CODE_STATE,
  REFERRAL_CODE_EXAMPLE,
  type ReferralCodeState,
} from "@/lib/referrals";

/**
 * "COLIVIQ-" is eight characters before a single digit exists, so a shorter
 * floor would fire a lookup on the bare stem. Wait until what has been typed
 * could plausibly be a whole code.
 */
const MIN_CHECK_LENGTH = "COLIVIQ-".length + 4;

export function ReferralCodeField({
  language,
  value,
  onChange,
  disabled,
}: {
  language: "en" | "de";
  value: ReferralCodeState;
  onChange: (next: ReferralCodeState) => void;
  disabled?: boolean;
}) {
  const de = language === "de";
  const [raw, setRaw] = useState(value.code);
  const [checking, setChecking] = useState(false);

  // Every keystroke starts a lookup; only the newest one may write the result.
  // Without this a slow response for "COLIVIQ-48392" can land after the fast
  // one for "COLIVIQ-483920" and mark a good code invalid.
  const requestSeq = useRef(0);

  useEffect(() => {
    const code = normaliseReferralCode(raw);

    if (!code) {
      setChecking(false);
      onChange(EMPTY_REFERRAL_CODE_STATE);
      return undefined;
    }

    // Still being typed. Checking now would flash a red "we don't recognise
    // this" at someone who is only halfway through the code.
    if (code.length < MIN_CHECK_LENGTH) {
      setChecking(false);
      onChange({ code, valid: null, referrerName: null });
      return undefined;
    }

    setChecking(true);
    const seq = ++requestSeq.current;
    const timer = window.setTimeout(async () => {
      try {
        const { data, error } = await supabase.rpc("resolve_referral_code", {
          _code: code,
        });
        if (error) throw new Error(error.message);
        // jsonb: { valid, referrer_name }, or null.
        const res = data as {
          valid?: boolean;
          referrer_name?: string | null;
        } | null;
        if (seq !== requestSeq.current) return;
        onChange({
          code,
          valid: !!res?.valid,
          referrerName: res?.referrer_name ?? null,
        });
      } catch {
        // A lookup that fails must not read as "your code is wrong". Leave the
        // verdict unknown; the server revalidates on submit either way.
        if (seq !== requestSeq.current) return;
        onChange({ code, valid: null, referrerName: null });
      } finally {
        if (seq === requestSeq.current) setChecking(false);
      }
    }, 400);

    return () => window.clearTimeout(timer);
    // onChange is a fresh closure on every parent render; depending on it would
    // restart the debounce on unrelated re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw]);

  return (
    <div className="rounded-xl border border-dashed border-primary/50 bg-primary/[0.03] p-4">
      <Label
        htmlFor="referralCode"
        className="flex items-center gap-2 text-sm font-semibold text-primary"
      >
        <Share2 className="h-4 w-4" />
        {de ? "Empfehlungscode (optional)" : "Referral code (optional)"}
      </Label>

      <p className="mb-3 mt-1.5 text-xs leading-relaxed text-muted-foreground">
        {de
          ? "Wurdest du von einer Person empfohlen, die bereits bei coliviq wohnt? Gib ihren Code ein – er bleibt mit deiner Bewerbung verknüpft."
          : "Were you referred by someone who already lives at coliviq? Enter their code — it stays attached to your application."}
      </p>

      <div className="relative">
        <Input
          id="referralCode"
          name="referralCode"
          value={raw}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          placeholder={REFERRAL_CODE_EXAMPLE}
          onChange={(e) => setRaw(e.target.value)}
          className="pr-10 font-semibold tracking-wide"
          aria-describedby="referralCodeStatus"
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
          {checking ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : value.valid === true ? (
            <CheckCircle2 className="h-4 w-4 text-primary" />
          ) : value.valid === false ? (
            <TriangleAlert className="h-4 w-4 text-destructive" />
          ) : null}
        </span>
      </div>

      <p
        id="referralCodeStatus"
        aria-live="polite"
        className="mt-2 text-xs font-medium"
      >
        {value.valid === true && (
          <span className="text-primary">
            {value.referrerName
              ? de
                ? `Gültiger Code – empfohlen von ${value.referrerName}.`
                : `Valid code — referred by ${value.referrerName}.`
              : de
                ? "Gültiger Code."
                : "Valid code."}
          </span>
        )}
        {value.valid === false && (
          <span className="text-destructive">
            {de
              ? "Diesen Code kennen wir nicht. Frag noch einmal nach – oder lass das Feld leer, deine Bewerbung geht so oder so raus."
              : "We don't recognise this code. Check it with your friend — or leave it empty, your application is sent either way."}
          </span>
        )}
      </p>
    </div>
  );
}

export default ReferralCodeField;
